import { Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { CV_BANK_SOURCE_LABELS, type CvBankEntryView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { CvViewer } from '../../../components/cv/CvViewer';
import { Drawer } from '../../../components/Drawer';
import { Popover, usePopover } from '../../../components/Popover';
import { formatDate, formatNumber } from '../../../lib/format';
import styles from '../CvBank.module.css';
import type { EntrySteps } from '../cv-bank-format';
import { RemovePanel } from './RemovePanel';

interface CvBankEntryDrawerProps {
  readonly entry: CvBankEntryView | null;
  readonly canEdit: boolean;
  readonly onClose: () => void;
  readonly onRemove: (entry: CvBankEntryView, reason: string) => void;
  /** Previous / "N of M" / Next through the page's rows; absent for an entry opened from a link. */
  readonly steps?: EntrySteps | null;
  readonly onStep?: (entryId: string) => void;
}

/** A row opened: the CV itself (a PDF renders here, a Word file downloads) and "Remove from CV Bank". */
export function CvBankEntryDrawer({ entry, canEdit, onClose, onRemove, steps = null, onStep }: CvBankEntryDrawerProps): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  if (!entry) return null;
  const contact = [entry.candidate.email, entry.candidate.phone].filter((value) => value !== null && value !== '').join(' · ');

  return (
    <Drawer
      isOpen
      size="wide"
      onClose={onClose}
      title={entry.candidate.fullName}
      meta={`${entry.cv.fileName} · added ${formatDate(entry.addedAt)}`}
      headerActions={steps && onStep ? <StepButtons steps={steps} onStep={onStep} /> : undefined}
      footer={
        canEdit ? (
          <Button ref={triggerRef} variant="danger" icon={Trash2} onClick={toggle}>
            Remove from CV Bank
          </Button>
        ) : undefined
      }
    >
      <div className={styles.drawerBody}>
        <p className={styles.facts}>
          <span>{contact === '' ? '—' : contact}</span>
          <span>{entry.lastJobTitle ? `Last applied to ${entry.lastJobTitle}` : 'Never applied to a job'}</span>
          <span>
            {CV_BANK_SOURCE_LABELS[entry.source]}
            {entry.addedByName ? `, by ${entry.addedByName}` : ''}
          </span>
          <Link to="/candidates/$candidateId" params={{ candidateId: entry.candidate.id }} className={styles.name}>
            Open their profile
          </Link>
        </p>
        <div className={styles.drawerViewer}>
          <CvViewer cv={entry.cv} candidateName={entry.candidate.fullName} fallback="Their details are on their profile." />
        </div>
      </div>
      <Popover anchor={anchor} onClose={close} align="right" width={360} label="Remove from CV Bank">
        <RemovePanel
          who={entry.candidate.fullName}
          onCancel={close}
          onRemove={(reason) => {
            close();
            onRemove(entry, reason);
          }}
        />
      </Popover>
    </Drawer>
  );
}

function StepButtons({ steps, onStep }: { readonly steps: EntrySteps; readonly onStep: (entryId: string) => void }): ReactNode {
  const { previousId, nextId } = steps;
  return (
    <>
      <Button size="sm" variant="outline" icon={ChevronLeft} aria-label="Previous CV" disabled={previousId === null} onClick={() => {
          if (previousId) onStep(previousId);
        }} />
      <span className={styles.stepPosition} aria-live="polite">
        {formatNumber(steps.position)} of {formatNumber(steps.total)}
      </span>
      <Button size="sm" variant="outline" icon={ChevronRight} aria-label="Next CV" disabled={nextId === null} onClick={() => {
          if (nextId) onStep(nextId);
        }} />
    </>
  );
}
